import React, { useState } from 'react';
import { ArrowRight, Shield, Search, AlertTriangle, FileText, CheckCircle, Zap, Database, Globe } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import ConsultationModal from '../../components/ConsultationModal';
import DemoModal from '../../components/DemoModal';

const VulnerabilityAssessmentPage: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isConsultationModalOpen, setIsConsultationModalOpen] = useState(false);
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);

  const assessmentTypes = [
    {
      icon: Globe,
      title: "Network Vulnerability Assessment",
      description: "Comprehensive scanning and analysis of your network infrastructure to identify security weaknesses and misconfigurations.",
      features: ["Port scanning", "Service enumeration", "Configuration analysis", "Network topology mapping"]
    },
    {
      icon: Database,
      title: "Web Application Assessment",
      description: "Automated and manual testing of web applications to identify OWASP Top 10 vulnerabilities and custom security flaws.",
      features: ["SQL injection detection", "XSS vulnerability scanning", "Authentication testing", "Session management analysis"]
    },
    {
      icon: Shield,
      title: "Infrastructure Assessment",
      description: "Evaluation of servers, databases, and cloud infrastructure for security vulnerabilities and compliance gaps.",
      features: ["Operating system vulnerabilities", "Database security assessment", "Cloud configuration review", "Patch management analysis"]
    },
    {
      icon: Zap,
      title: "Wireless Security Assessment",
      description: "Assessment of wireless networks and devices to identify security weaknesses in WiFi and mobile connectivity.",
      features: ["WiFi security analysis", "Rogue access point detection", "Encryption strength testing", "Mobile device security"]
    }
  ];

  const process = [
    {
      title: "Asset Discovery",
      description: "Identify and catalog all assets within the assessment scope including networks, systems, and applications.",
      icon: Search
    },
    {
      title: "Vulnerability Scanning",
      description: "Automated scanning using industry-leading tools to identify known vulnerabilities and security weaknesses.",
      icon: AlertTriangle
    },
    {
      title: "Manual Verification",
      description: "Expert manual verification of findings to eliminate false positives and identify complex vulnerabilities.",
      icon: Shield
    },
    {
      title: "Risk Analysis",
      description: "Comprehensive risk assessment and prioritization based on business impact and exploitability.",
      icon: FileText
    }
  ];

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDarkMode ? 'bg-slate-950' : 'bg-slate-50'
    }`}>
      <Header 
        isMenuOpen={isMenuOpen} 
        setIsMenuOpen={setIsMenuOpen}
        isDarkMode={isDarkMode}
        toggleDarkMode={() => setIsDarkMode(!isDarkMode)}
      />

      <ConsultationModal 
        isOpen={isConsultationModalOpen} 
        onClose={() => setIsConsultationModalOpen(false)} 
      />
      <DemoModal 
        isOpen={isDemoModalOpen} 
        onClose={() => setIsDemoModalOpen(false)} 
      />

      {/* Hero Section */}
      <section className={`relative py-20 overflow-hidden ${
        isDarkMode 
          ? 'bg-gradient-to-br from-slate-950 via-slate-900 to-purple-950 text-slate-100' 
          : 'bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 text-slate-900'
      }`}>
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/5380664/pexels-photo-5380664.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop"
            alt="Vulnerability Assessment"
            className="w-full h-full object-cover opacity-10"
          />
          <div className={`absolute inset-0 ${
            isDarkMode 
              ? 'bg-gradient-to-r from-slate-950/90 to-purple-950/90' 
              : 'bg-gradient-to-r from-purple-50/90 to-indigo-50/90'
          }`} />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <Search className={`h-16 w-16 mx-auto mb-6 ${
              isDarkMode ? 'text-purple-400' : 'text-purple-600'
            }`} />
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Vulnerability <span className={isDarkMode ? "text-purple-400" : "text-purple-600"}>Assessment</span>
            </h1>
            <p className={`text-xl md:text-2xl mb-8 max-w-3xl mx-auto ${
              isDarkMode ? "text-slate-300" : "text-slate-600"
            }`}>
              Proactively identify and prioritize security vulnerabilities across your entire 
              IT infrastructure before they can be exploited by attackers.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => setIsConsultationModalOpen(true)}
                className={`px-8 py-4 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center transform hover:scale-105 shadow-lg ${
                  isDarkMode 
                    ? "bg-purple-600 hover:bg-purple-700 text-white hover:shadow-purple-500/25" 
                    : "bg-purple-600 hover:bg-purple-700 text-white hover:shadow-purple-500/25"
                }`}
              >
                <Shield className="mr-2 h-5 w-5" />
                Start Assessment
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
              <button 
                onClick={() => setIsDemoModalOpen(true)}
                className={`px-8 py-4 rounded-lg font-semibold transition-all duration-300 border-2 ${
                  isDarkMode 
                    ? "border-purple-400 text-purple-400 hover:bg-purple-400 hover:text-slate-900" 
                    : "border-purple-600 text-purple-600 hover:bg-purple-600 hover:text-white"
                }`}
              >
                View Sample Report
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Assessment Types Section */}
      <section className={`py-20 ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${
              isDarkMode ? 'text-slate-100' : 'text-slate-900'
            }`}>
              Comprehensive Assessment Coverage
            </h2>
            <p className={`text-xl max-w-3xl mx-auto ${
              isDarkMode ? 'text-slate-300' : 'text-slate-600'
            }`}>
              Our vulnerability assessments cover all critical areas of your IT infrastructure
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {assessmentTypes.map((type, index) => (
              <div key={index} className={`p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow ${
                isDarkMode ? 'bg-slate-800 border border-slate-700' : 'bg-slate-50 border border-slate-200'
              }`}>
                <type.icon className={`h-12 w-12 mb-4 ${
                  isDarkMode ? 'text-purple-400' : 'text-purple-600'
                }`} />
                <h3 className={`text-xl font-semibold mb-3 ${
                  isDarkMode ? 'text-slate-100' : 'text-slate-900'
                }`}>{type.title}</h3>
                <p className={`mb-4 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  {type.description}
                </p>
                <ul className="space-y-2">
                  {type.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center space-x-2">
                      <CheckCircle className={`h-4 w-4 ${
                        isDarkMode ? 'text-green-400' : 'text-green-600'
                      }`} />
                      <span className={`text-sm ${
                        isDarkMode ? 'text-slate-300' : 'text-slate-600'
                      }`}>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className={`py-20 ${isDarkMode ? 'bg-slate-950' : 'bg-slate-50'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${
              isDarkMode ? 'text-slate-100' : 'text-slate-900'
            }`}>
              Our Assessment Process
            </h2>
            <p className={`text-xl max-w-3xl mx-auto ${
              isDarkMode ? 'text-slate-300' : 'text-slate-600'
            }`}>
              A systematic approach to identify and prioritize vulnerabilities
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <div key={index} className={`text-center p-6 rounded-xl ${
                isDarkMode ? 'bg-slate-900' : 'bg-white'
              } shadow-lg`}>
                <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
                  isDarkMode ? 'bg-purple-600' : 'bg-purple-100'
                }`}>
                  <step.icon className={`h-8 w-8 ${
                    isDarkMode ? 'text-white' : 'text-purple-600'
                  }`} />
                </div>
                <h3 className={`text-lg font-semibold mb-2 ${
                  isDarkMode ? 'text-slate-100' : 'text-slate-900'
                }`}>{step.title}</h3>
                <p className={`text-sm ${
                  isDarkMode ? 'text-slate-300' : 'text-slate-600'
                }`}>{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className={`py-20 ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <img 
                src="https://images.pexels.com/photos/5380665/pexels-photo-5380665.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop"
                alt="Vulnerability Assessment Dashboard"
                className="rounded-xl shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-xl" />
            </div>
            
            <div>
              <h2 className={`text-3xl md:text-4xl font-bold mb-6 ${
                isDarkMode ? 'text-slate-100' : 'text-slate-900'
              }`}>
                Continuous Security Monitoring
              </h2>
              <p className={`text-lg mb-6 ${
                isDarkMode ? 'text-slate-300' : 'text-slate-600'
              }`}>
                Our vulnerability assessment service provides ongoing monitoring and regular 
                assessments to ensure your security posture remains strong against evolving threats.
              </p>
              <div className="space-y-4">
                {[
                  "Automated vulnerability scanning",
                  "Risk-based prioritization",
                  "Compliance reporting",
                  "Remediation guidance",
                  "Executive dashboards"
                ].map((benefit, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle className={`h-5 w-5 ${
                      isDarkMode ? 'text-green-400' : 'text-green-600'
                    }`} />
                    <span className={isDarkMode ? 'text-slate-300' : 'text-slate-600'}>
                      {benefit}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer 
        onConsultationClick={() => setIsConsultationModalOpen(true)}
        onDemoClick={() => setIsDemoModalOpen(true)}
        isDarkMode={isDarkMode}
      />
    </div>
  );
};

export default VulnerabilityAssessmentPage;